import { customAlphabet } from 'nanoid';
import { flattenObject, } from '../../utils';
import { LogLevel, LogLevelNumber, logLevelStringToNumber, } from './types';
import ExternalLoggerService, { Level, } from './external';
// Note: with this alphabet and 12 chars, there is roughly a 1% chance of at least one collision after about 28 days.
// This is assuming 100.000 ids per hour.
// src: https://zelark.github.io/nano-id-cc/
const sessionIdGenerator = customAlphabet('346789ABCDEFGHJKLMNPQRTUVWXY', 12);
let idIncrement = 0;
/**
 *  Service to create and process log messages
 */
export default class Logger {
    constructor(eventBus, config) {
        /**
         *  Severity log levels for individual processors
         */
        this._logLevelsConfig = {
            console: LogLevelNumber.Warning,
            service: LogLevelNumber.None,
        };
        /**
         *  Holds all values that will be injected in all log messages
         */
        this._staticValues = {};
        /**
         *  Holds the INTENDED entity id (e.g. shop guid, order guid, etc.).
         */
        this.entityId = '';
        this._id = config && config.sessionId ? config.sessionId : Logger.generateSessionId();
        this._eventBus = eventBus;
        // Relay to the actual event bus
        this._eventBus.on(['log'], (path, payload) => {
            let msg;
            // Handle non OpenTicketError errors
            if (payload && payload._isOpenTicketLogMessage) {
                msg = payload;
            }
            else {
                msg = this._buildFromError(payload, path);
            }
            this._send(msg);
        });
    }
    static generateSessionId() {
        return sessionIdGenerator();
    }
    get id() {
        return this._id;
    }
    /**
     *  Set the severity log levels
     *
     *  Any log message with a level smaller or equal should be processed.
     *
     *  @param logLevels Individual log level settings per processor.
     */
    setLoggingLevels(logLevels) {
        if (logLevels && logLevels.console && logLevelStringToNumber[logLevels.console]) {
            this._logLevelsConfig.console = logLevelStringToNumber[logLevels.console];
            // eslint-disable-next-line no-console
            console.info('Console log level changed', logLevels.console);
        }
        if (logLevels && logLevels.service && logLevelStringToNumber[logLevels.service]) {
            this._logLevelsConfig.service = logLevelStringToNumber[logLevels.service];
            // eslint-disable-next-line no-console
            console.info('Service log level changed', logLevels.service);
        }
    }
    /**
     *  Set the logging url for the external logging service
     *
     *  @param loggingUrl
     */
    setLoggingUrl(loggingUrl) {
        const proto = loggingUrl.startsWith('http://') ? 'http' : 'https';
        const host = loggingUrl
            .replace(/^(?:[a-z]+:)?\/\//, '')
            .replace(/\/*$/, '');
        const settings = {
            entityId: this.entityId || '',
            proto,
            host,
        };
        if (this._externalService) {
            // eslint-disable-next-line no-console
            console.info('Restart external log service', proto, host);
        }
        else {
            // eslint-disable-next-line no-console
            console.info('Start external log service', proto, host);
        }
        this._externalService = new ExternalLoggerService(settings);
    }
    /**
     *  Set the entity ID to be used with the logging service.
     *
     *  Note, this should be the INTENDED entity id (e.g. shop id, order id, etc.).
     *  As the validity is not yet known until after the logger is initialized!
     *
     *  @param entityId The intended entity id.
     */
    setEntityId(entityId) {
        this.entityId = entityId;
        this.addStaticValue('entity_id', entityId);
    }
    /**
     *  Add a static value to all log messages
     *
     *  @param key
     *  @param value
     */
    addStaticValue(key, value) {
        this._staticValues[key] = value;
    }
    /**
     *  Create and emit a log message
     *
     *  @param logLevel The log level of the log message
     *  @param path The event path of the log message. Also used to create the slug.
     *  @param message Add optional human readable message.
     *  @param data The extra data for the log message. Also used for translation placeholders.
     *  @param parent When the log messages is a wrapper for another log message.
     */
    create(logLevel, path, data = {}, message = null, parent = null) {
        if (!path || !path.length) {
            path = ['unknown'];
        }
        path = ['external', ...path];
        return this._buildAndEmit(logLevel, path, data, message, parent);
    }
    /**
     *  Create and emit a log message
     *
     *  @param logLevel The log level of the log message
     *  @param path The event path of the log message. Also used to create the slug.
     *  @param message Add optional human readable message.
     *  @param data The extra data for the log message. Also used for translation placeholders.
     *  @param parent When the log messages is a wrapper for another log message.
     *  @param shouldEmit If the message should be processed.
     */
    _create(logLevel, path, data = {}, message = null, parent = null, shouldEmit = true) {
        return this._buildAndEmit(logLevel, path, data, message, parent, shouldEmit);
    }
    /**
     *  Pass-through the parent log message, if it is a log message.
     *  Create and emit a log message, if not.
     *
     *  @param logLevel The log level of the log message
     *  @param path The event path of the log message. Also used to create the slug.
     *  @param message Add optional human readable message.
     *  @param data The extra data for the log message. Also used for translation placeholders.
     *  @param parent When the log messages is a wrapper for another log message.
     *  @param shouldEmit If the message should be processed.
     */
    _passThroughOrCreate(parent, logLevel, path, data = {}, message = null, shouldEmit = true) {
        if (parent && parent._isOpenTicketLogMessage) {
            return parent;
        }
        return this._buildAndEmit(logLevel, path, data, message, parent, shouldEmit);
    }
    /**
     *  Send the log message to all processors.
     *
     *  If the log level is enough.
     *
     *  @param msg
     */
    _send(msg) {
        const at = new Date(msg.at);
        this._getConsoleForLoggingLevel(msg)(`[${msg.session} | ${msg.id} | ${at.getHours()}:${at.getMinutes()}] ${msg.slug}${msg.parent ? ` (^ ${msg.parent.id})` : ''}`, msg.message, msg.data);
        this._sendMessageToExternalService(msg);
    }
    /**
     *  Helper to create a new log message object and emit it
     *
     *  @param logLevel The log level of the log message
     *  @param path The event path of the log message. Also used to create the slug for the translatable message
     *  @param message Add optional human readable message
     *  @param data The extra data for the error. Also used for translation placeholders
     *  @param parent When the error is a wrapper for another error
     *  @param shouldEmit If the message should be processed.
     */
    _buildAndEmit(logLevel, path, data = {}, message = null, parent = null, shouldEmit = true) {
        if (!path || !path.length) {
            path = ['unknown'];
        }
        path = ['log', ...path];
        const err = this._build(logLevel, path, message, data, parent);
        if (shouldEmit) {
            try {
                // noinspection JSIgnoredPromiseFromCall
                this._eventBus.emit(path, err);
            }
            catch (e) {
                // Explicitly ignore any failures to emit!
            }
        }
        return err;
    }
    /**
     *  Helper to create a new log message from an error object
     *
     *  @param err The Error to transform to a log message object
     *  @param path Optionally, the error can have been emitted and thus will have a path/slug
     */
    _buildFromError(err, path) {
        let message = null;
        let data = {};
        if (err) {
            data = this._retrieveDataFromError(err);
            if (err.description && typeof err.description === 'string') {
                message = err.description;
            }
            else if (err.message && typeof err.message === 'string') {
                message = err.message;
            }
            else if (err.name && typeof err.name === 'string') {
                message = err.name;
            }
        }
        if (!path) {
            path = ['log', 'unknown'];
        }
        return this._build(LogLevel.Error, path, message, data);
    }
    /**
     *  Helper to create a new log message object
     *
     *  @param logLevel The severity level of the log message
     *  @param path The event path of the log message. Also used to create the slug for the translatable message
     *  @param message Add optional human readable message
     *  @param data The extra data for the log message. Also used for translation placeholders
     *  @param parent When the log message is a wrapper for another log message
     */
    _build(logLevel, path, message = null, data = {}, parent = null) {
        let logLevelNumber = logLevelStringToNumber[logLevel] || LogLevel.None;
        const logMessage = {
            _isOpenTicketLogMessage: true,
            session: this._id,
            id: ++idIncrement,
            at: Date.now(),
            slug: `sdkshop.${path.join('.')}`,
            data: {},
            exception: null,
            parent: null,
            displayLogLevel: logLevel,
            logLevel: logLevelNumber,
            message,
        };
        data = Object.assign(Object.assign({}, data), this._staticValues);
        try {
            if (parent && parent._isOpenTicketLogMessage) {
                logMessage.parent = parent;
            }
            else if (parent && parent instanceof Error) {
                logMessage.exception = parent;
                data = Object.assign(Object.assign({}, data), { exception: this._retrieveDataFromError(parent) });
            }
            else if (parent) {
                data = Object.assign(Object.assign({}, parent), data);
            }
        }
        catch (_) {
            // Explicitly ignore any data retrieval!
        }
        logMessage.data = data;
        return logMessage;
    }
    // noinspection JSMethodCanBeStatic
    /**
     *  Retrieve the data from the given error exception.
     *
     *  @param error The error exception
     */
    _retrieveDataFromError(error) {
        if (!error) {
            return {
                name: 'Error',
                message: 'Unknown error',
            };
        }
        const flatError = {
            name: error.name || 'Error',
            message: error.message || 'Unknown error',
            description: error.description,
            number: error.number,
            fileName: error.fileName,
            lineNumber: error.lineNumber,
            columnNumber: error.columnNumber,
        };
        if (error.response) {
            if (typeof error.response.status !== 'undefined' &&
                typeof error.response.statusText !== 'undefined') {
                flatError.status = error.response.status;
                flatError.statusText = error.response.statusText;
            }
            if (error.response.data) {
                if (typeof error.response.data.status_code !== 'undefined') {
                    flatError.status_code = error.response.data.status_code;
                }
                if (typeof error.response.data.error_description !== 'undefined') {
                    flatError.error_description = error.response.data.error_description;
                }
            }
        }
        if (error.stack) {
            flatError.stack = error.stack;
            const stack = typeof error.stack === 'string' ? error.stack.split('\n') : error.stack;
            flatError.stackList = stack.map((part) => part.trim())
                .filter((part) => part && part.length);
        }
        if (error.slug) {
            flatError.slug = error.slug;
            if (error.slugData) {
                return Object.assign(Object.assign({}, error.slugData), flatError);
            }
        }
        return flatError;
    }
    /**
     *  Retrieve the correct console processor.
     *
     *  Depends on the console logging level.
     *
     *  @param msg
     */
    _getConsoleForLoggingLevel(msg) {
        // If the logging level is higher (e.g.: Less important)
        // than the set level, return a no-op function
        if (msg.logLevel > this._logLevelsConfig.console) {
            return () => {
                // No-op
            };
        }
        switch (msg.logLevel) {
            case LogLevelNumber.Debug:
                // eslint-disable-next-line no-console
                return console.debug;
            case LogLevelNumber.Info:
                // eslint-disable-next-line no-console
                return console.info;
            case LogLevelNumber.Warning:
                return console.warn;
            case LogLevelNumber.Error:
            case LogLevelNumber.Fatal:
            default:
                return console.error;
        }
    }
    /**
     *  Send the message to the external logging service.
     *
     *  Depends on the service logging level.
     *
     *  @param msg
     */
    _sendMessageToExternalService(msg) {
        // If the logging level is higher (e.g.: Less important)
        // than the set level, abort
        if (!this._externalService || msg.logLevel > this._logLevelsConfig.service) {
            return;
        }
        let logLevel;
        switch (msg.logLevel) {
            case LogLevelNumber.Fatal:
                logLevel = Level.Fatal;
                break;
            case LogLevelNumber.Error:
                logLevel = Level.Error;
                break;
            case LogLevelNumber.Warning:
                logLevel = Level.Warn;
                break;
            case LogLevelNumber.Debug:
                logLevel = Level.Debug;
                break;
            case LogLevelNumber.Info:
            default:
                logLevel = Level.Info;
                break;
        }
        // Create raw data json string
        const rawData = JSON.stringify(msg.data);
        // Recreate object from json
        // This ensures any complex objects are filtered out (like functions) and
        // literal objects are converted to their literal value (like String{})
        // This allows for every Object to be flattened,
        // without losing or corrupting data
        const flatData = flattenObject({ data: JSON.parse(rawData) });
        this._externalService.log(logLevel, msg.slug, Object.assign(Object.assign({}, flatData), { raw_data: rawData, entity_id: this.entityId || '', session: msg.session, id: JSON.stringify(msg.id), at: JSON.stringify(msg.at), parent: msg.parent ? JSON.stringify(msg.parent.id) : '', slug: msg.slug, message: msg.message || '' }));
    }
}
//# sourceMappingURL=logger.js.map